import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import cn from "clsx";
import { Scrollbars } from "react-custom-scrollbars";
import { useTranslation } from "react-i18next";
import isEmpty from "lodash/isEmpty";

import FilterElement from "./filterElement";
import FilterOptionItem from "./filterOptionItem";
import useMultiselectFilter from "../hooks/useMultiselectFilter";
import useSelectedByResponse from "../hooks/useSelectedByResponse";
import { useServerSearch } from "../hooks/useServerSearch";
import { SelectedFiltersContext } from "../webFilteringSection";
import { GeneralFacet, MultiSelectFacet, Source } from "../types";

type FilterMultiSelectProps = {
  filter: Omit<GeneralFacet & MultiSelectFacet, "sortOrder"> & {
    search?: {
      enabled?: boolean;
      placeholder?: string;
      source?: Source;
    };
  };
};

const FilterMultiSelectWeb: FC<FilterMultiSelectProps> = ({ filter }) => {
  const { t } = useTranslation("productListing");
  const [searchValue, setSearchValue] = useState("");
  const { onSelection, selectedFilters } = useContext(SelectedFiltersContext);
  const {
    filterOptions,
    selectedOptions,
    onFilterOptionClick,
    clearSelectedOptions,
    setSelectedOptions
  } = useMultiselectFilter(filter, onSelection);

  const flattenedOptionsForSelection = useMemo(() => {
    if (
      filter.viewType === "groups" &&
      filterOptions?.length > 0 &&
      filterOptions[0]?.data
    ) {
      return filterOptions.flatMap(
        (group: { data?: unknown[] }) => group.data || []
      );
    }
    return filterOptions || [];
  }, [filter.viewType, filterOptions]);

  const selectedByResponse = useSelectedByResponse(
    flattenedOptionsForSelection
  );

  const queryParam = filter.queryParam;
  const selectedOptionsFromContext =
    selectedFilters?.[queryParam]?.selectedOptions;

  /** String signature so the effect does not re-run on new array references with the same keys. */
  const selectionSyncKey = useMemo(() => {
    const toSet = !isEmpty(selectedOptionsFromContext)
      ? selectedOptionsFromContext
      : selectedByResponse;
    return (toSet ?? [])
      .map(o => o.key)
      .sort()
      .join("|");
  }, [selectedOptionsFromContext, selectedByResponse]);

  useEffect(() => {
    const fromFilters = selectedFilters?.[queryParam]?.selectedOptions;
    const toSet = !isEmpty(fromFilters) ? fromFilters : selectedByResponse;
    const next = toSet || [];

    setSelectedOptions(prev => {
      const prevKeys = prev
        .map(o => o.key)
        .sort()
        .join("|");
      const nextKeys = next
        .map(o => o.key)
        .sort()
        .join("|");
      if (prevKeys === nextKeys) return prev;
      return next;
    });
  }, [selectionSyncKey, queryParam, setSelectedOptions]);

  const { isServerSearch, serverOptions, isSearchable } = useServerSearch(
    filter,
    searchValue
  );

  const handleSearchType = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value.toLocaleLowerCase());
  };

  const handleClearFilter = () => {
    if (searchValue) setSearchValue("");
    if (selectedOptions.length) clearSelectedOptions();
  };

  const selectedOptionsCount = useMemo(() => {
    const availableKeys = new Set(
      (flattenedOptionsForSelection as any[])
        .filter(o => o.count == null || Number(o.count) > 0)
        .map(o => o.key)
    );
    return selectedOptions.filter(o => availableKeys.has(o.key)).length;
  }, [flattenedOptionsForSelection, selectedOptions]);

  const optionsToRender: any[] = useMemo(() => {
    if (isServerSearch) {
      if (serverOptions === null) {
        return filterOptions;
      }
      if (!isEmpty(serverOptions) && !isEmpty(searchValue)) {
        return serverOptions;
      }
      return serverOptions;
    }

    return filterOptions.filter(option =>
      option.label.toLocaleLowerCase().includes(searchValue)
    );
  }, [isServerSearch, filterOptions, serverOptions, searchValue]);

  const selectedKeySet = useMemo(
    () => new Set(selectedOptions.map(s => s.key)),
    [selectedOptions]
  );

  const filterOptionList = useMemo(() => {
    const renderOption = (option: { key: string }) => (
      <FilterOptionItem
        key={option.key}
        option={option as any}
        isSelected={selectedKeySet.has(option.key)}
        onSelection={onFilterOptionClick}
      />
    );

    if (filter.viewType !== "groups") {
      return optionsToRender.map(option => renderOption(option));
    }

    const hasGroupStructure =
      optionsToRender.length > 0 && optionsToRender[0]?.data !== undefined;

    if (isServerSearch && !isEmpty(searchValue) && !hasGroupStructure) {
      return optionsToRender.map(option => renderOption(option));
    }

    return filterOptions.map((group, groupIndex) => {
      const filteredGroupData = group.data.filter(option =>
        option.label.toLocaleLowerCase().includes(searchValue)
      );

      if (filteredGroupData.length === 0) {
        return null;
      }

      return (
        <li
          className="filter_options_type"
          key={group.label ?? `group-${groupIndex}`}
        >
          <span className="filter_type_label">{group.label}</span>
          <ul>{filteredGroupData.map(option => renderOption(option))}</ul>
        </li>
      );
    });
  }, [
    filter.viewType,
    filterOptions,
    isServerSearch,
    optionsToRender,
    searchValue,
    selectedKeySet,
    onFilterOptionClick
  ]);

  return (
    <>
      <FilterElement
        className={cn({ items_selected: selectedOptionsCount })}
        filterLabel={filter.label}
        filterSelectedCount={selectedOptionsCount}
        filterWrapperClassName="mxw-300"
        setSearchValue={setSearchValue}
      >
        <div>
          {isSearchable && (
            <div className="brand-search-wrapper">
              <input
                type="text"
                placeholder={filter.search.placeholder}
                value={searchValue}
                onChange={handleSearchType}
                aria-label={`Search for ${filter.label}`}
              />
              <span className="search_icon" />
              {searchValue && (
                <span
                  className="cross_icon"
                  onClick={() => setSearchValue("")}
                />
              )}
            </div>
          )}
          <Scrollbars
            autoHeight
            autoHeightMin={55}
            autoHeightMax={isSearchable ? 193 : 240}
          >
            <ul className="hierarchical_categories2_item">
              {filterOptionList}
            </ul>
          </Scrollbars>
        </div>

        <div className="count_selected">
          {selectedOptionsCount} selected
          <span onClick={handleClearFilter}>{t("clear")}</span>
        </div>
      </FilterElement>
    </>
  );
};

export default FilterMultiSelectWeb;
